import React, { useState } from 'react';
import { Anchor, AlertTriangle, Plus, Search } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import { useStorageState } from '../../../../hooks/useStorageState';
import TowingOperationsForm from './TowingOperationsForm';

/**
 * Towing Operations Tab - Track towing operations and bollard pull records
 */
const TowingOperationsTab = ({ currentVessel, onAddEntry }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Storage for towing operations
  const storageKey = currentVessel?.id ? `towing_operations_${currentVessel.id}` : 'towing_operations';
  const { value: towingOps, setValue: saveTowingOps } = useStorageState(storageKey, []);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    endTime: '',
    operationType: 'harbor', // harbor, ocean, salvage, escort
    towedVessel: {
      name: '',
      type: '',
      grt: '',
      length: ''
    },
    towlineConfiguration: {
      type: 'wire', // wire, synthetic, chain, combination
      diameter: '',
      length: '',
      workingLoad: ''
    },
    startPosition: { latitude: '', longitude: '' },
    endPosition: { latitude: '', longitude: '' },
    weather: {
      windSpeed: '',
      windDirection: '',
      seaState: '',
      visibility: ''
    },
    bollardPull: '',
    averageSpeed: '',
    maxSpeed: '',
    fuelConsumed: '',
    incidentReports: '',
    masterName: '',
    notes: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newTow = {
      id: Date.now().toString(),
      ...formData,
      timestamp: new Date(`${formData.date}T${formData.startTime}`).toISOString(),
      vesselName: currentVessel?.name,
      duration: calculateDuration(formData.startTime, formData.endTime)
    };
    
    const updatedOps = [newTow, ...towingOps].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    await saveTowingOps(updatedOps);
    
    // Also add to logbook
    onAddEntry({
      type: 'TOWING_OPS',
      category: 'operations',
      summary: `Towing operation: ${formData.towedVessel.name}`,
      description: `${formData.operationType} tow - ${formData.towlineConfiguration.type} towline`,
      details: newTow
    });
    
    setShowAddForm(false);
    resetForm();
  };
  
  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      startTime: new Date().toTimeString().slice(0, 5),
      endTime: '',
      operationType: 'harbor',
      towedVessel: {
        name: '',
        type: '',
        grt: '',
        length: ''
      },
      towlineConfiguration: {
        type: 'wire',
        diameter: '',
        length: '',
        workingLoad: ''
      },
      startPosition: { latitude: '', longitude: '' },
      endPosition: { latitude: '', longitude: '' },
      weather: {
        windSpeed: '',
        windDirection: '',
        seaState: '',
        visibility: ''
      },
      bollardPull: '',
      averageSpeed: '',
      maxSpeed: '',
      fuelConsumed: '',
      incidentReports: '',
      masterName: '',
      notes: ''
    });
  };
  
  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    const startMin = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMin = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
    const duration = endMin - startMin;
    if (duration < 0) return '';
    return `${Math.floor(duration / 60)}h ${duration % 60}m`;
  };
  
  const filteredOps = towingOps.filter(op => 
    searchQuery === '' || 
    JSON.stringify(op).toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const stats = {
    totalTows: towingOps.length,
    thisMonth: towingOps.filter(op => {
      const date = new Date(op.timestamp);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    totalHours: towingOps.reduce((acc, op) => {
      const match = op.duration?.match(/(\d+)h/);
      return acc + (match ? parseInt(match[1]) : 0);
    }, 0),
    lastTow: towingOps[0]?.timestamp ? new Date(towingOps[0].timestamp).toLocaleDateString() : 'N/A'
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Anchor className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Towing Operations Log</h2>
                <p className="text-sm text-gray-600">Record towing operations and bollard pull data</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Tow
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Tows</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalTows}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">This Month</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.thisMonth}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Hours</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalHours}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Last Tow</p>
            <p className="text-sm font-medium text-gray-900 mt-1">{stats.lastTow}</p>
          </div>
        </div>
      </div>
      
      {/* Safety Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Towing Safety Requirements:</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Record towline specifications and working loads</li>
              <li>• Document weather conditions and sea state</li>
              <li>• Maintain bollard pull certificates and test records</li>
              <li>• Log all incidents, near-misses, and line failures</li>
              <li>• Follow IMO MSC/Circ.884 guidelines for towing operations</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Add Form */}
      {showAddForm && (
        <TowingOperationsForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      
      {/* Towing History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Towing History</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search operations..."
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Towed Vessel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Towline</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weather</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOps.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                    No towing operations recorded
                  </td>
                </tr>
              ) : (
                filteredOps.map(op => (
                  <tr key={op.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(op.timestamp).toLocaleDateString()} {op.startTime}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {op.towedVessel.name}
                      {op.towedVessel.grt && <span className="text-xs text-gray-500 ml-1">({op.towedVessel.grt} GRT)</span>}
                    </td>
                    <td className="px-6 py-4">
                      <Badge color={
                        op.operationType === 'salvage' ? 'red' : 
                        op.operationType === 'ocean' ? 'blue' : 
                        'gray'
                      } size="sm">
                        {op.operationType}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {op.duration || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {op.towlineConfiguration.type} {op.towlineConfiguration.diameter && `${op.towlineConfiguration.diameter}mm`}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {op.weather.seaState || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TowingOperationsTab;